
    // IMPORTS
    import { defineComponent, PropType, computed, ref, onMounted, watch, reactive } from 'vue'

    import { Grid, GridCellProps, GridColumnProps, GridDataStateChangeEvent, GridExpandChangeEvent, GridColumnMenuCheckboxFilter } from '@progress/kendo-vue-grid'
    import LoaderGuard from '@/components/common/loader-guard.vue'
    import { DateField, BoolField } from './fields'

    import { CompositeFilterDescriptor, DataResult, FilterDescriptor, GroupDescriptor, isCompositeFilterDescriptor, process, SortDescriptor, State } from '@progress/kendo-data-query'
    import { PageChangeEvent } from '@progress/kendo-vue-dropdowns/dist/npm/common/events'
    import { IGridState } from '@/types/userState'
    import { gridDefaultRenderCell } from './cells'
    import { buildLocalization, ItemScroller } from '@/util'

    export default defineComponent({
        name: 'myGrid',

        components: {
            LoaderGuard,
            'k-grid': Grid,
            gridDefaultRenderCell,
            DateField,
            BoolField,
            'grid-column-menu-checkbox-filter': GridColumnMenuCheckboxFilter
        },

        props: {
            data: {
                type: Array as PropType<any[] | null>,
                default: null,
                validator: v => Array.isArray(v) || v == null
            },
            columns: { type: Array as PropType<GridColumnProps[]>, required: true },
            withHover: { type: Boolean as PropType<boolean>, required: false, default: () => false },
            sortable: { type: Boolean as PropType<boolean>, required: false, default: () => true },
            reorderable: { type: Boolean as PropType<boolean>, required: false, default: () => true },
            resizable: { type: Boolean as PropType<boolean>, required: false, default: () => true },
            filterable: { type: Boolean as PropType<boolean>, required: false, default: () => false },
            groupable: { type: Boolean as PropType<boolean>, required: false, default: () => false },
            pageable: { type: Boolean as PropType<boolean>, required: false, default: () => false },
            pageSize: { type: Number as PropType<number>, required: false, default: () => 25 },
            initialSort: { type: Object as PropType<SortDescriptor[]>, required: false, default: () => null },
            initialGroup: { type: Object as PropType<GroupDescriptor[]>, required: false, default: () => null },
            state: { type: Function as PropType<() => IGridState | null>, required: false, default: () => null },
        },

        setup(props) {
            /********************************************************************************************************************
             INIT
             ********************************************************************************************************************/
            const itemScroller = new ItemScroller

            /********************************************************************************************************************
             PRIVATE DATA
             ********************************************************************************************************************/
            const defaultState = ({
                take: props.pageable
                    ? props.pageSize
                    : 1000,
                skip: 0,
                filter: {
                    logic: 'and',
                    filters: []
                },
                sort: props.initialSort ?? [],
                group: props.initialGroup ?? []
            } as State)

            /********************************************************************************************************************
             PUBLIC DATA
             ********************************************************************************************************************/
            const gridData = ref<DataResult | null>(null)

            const state = reactive<IGridState>(
                {
                    gridState: defaultState,
                    idOfCurrentRow: null
                })

            const publicData = { gridData, state }

            /********************************************************************************************************************
             COMPUTED DATA
             ********************************************************************************************************************/
            const data = computed(() => props.data)
            const hasLoaded = computed(() => data.value?.length !== undefined)

            const gridState = computed(() => state.gridState)

            const computedData = { data, hasLoaded, gridState }

            /********************************************************************************************************************
             PUBLIC METHODS
             ********************************************************************************************************************/
            const { localize } = buildLocalization()

            function dataStateChange(event: GridDataStateChangeEvent) {
                setGridState(event.data)
            }

            function expandChange(event: GridExpandChangeEvent) {
                event.dataItem[event.target.$props.expandField] = event.value
            }

            function onPageChange(event: PageChangeEvent) {
                if (gridState.value != null) {
                    gridState.value.skip = event.page.skip
                    gridState.value.take = event.page.take

                    persistState()
                    processData()
                }
            }

            function cellClick(dataItem: any) {
                dataItem.expanded = dataItem.expanded === undefined
                    ? false
                    : !dataItem.expanded
            }

            function isNotGroupHeader(props: GridCellProps): boolean {
                return props.rowType !== 'groupHeader'
            }

            function onCheckboxFilterChange(props: any, event: CompositeFilterDescriptor) {
                if (gridState.value) {
                    gridState.value.filter = event
                }

                persistState()
                processData()
            }

            const publicMethods = {
                localize,
                dataStateChange,
                expandChange,
                onPageChange,
                cellClick,
                isNotGroupHeader,
                onCheckboxFilterChange
            }

            /********************************************************************************************************************
             PRIVATE METHODS
             ********************************************************************************************************************/
            function initializeState() {
                if (props.state != null) {
                    const inputState = props.state()
                    state.gridState = inputState?.gridState ?? defaultState
                    state.idOfCurrentRow = inputState?.idOfCurrentRow ?? null
                }

                const idOfCurrentRow = state.idOfCurrentRow
                if (idOfCurrentRow) {
                    itemScroller.scrollToItem(() => document.getElementById(idOfCurrentRow), false)
                    state.idOfCurrentRow = null // do not scroll on next page entry
                }

                processData()
            }

            function setGridState(newState: State) {
                state.gridState = { ...newState }
                persistState()
                processData()
            }

            function persistState() {
                if (props.state != null) {
                    const persistedState = props.state()

                    if (persistedState != null) {
                        persistedState.gridState = { ...state.gridState }
                    }
                }
            }

            function processData() {
                updateCellHeaders()
                gridData.value = process(props.data ?? [], { ...gridState.value })
            }

            function updateCellHeaders() {
                // Reset header templates
                props.columns.forEach(col => col.headerCell = null)

                // Apply new header templates
                const currentFilters = gridState.value?.filter?.filters
                if (currentFilters) {
                    updateCellHeadersRecursively(currentFilters)
                }
            }

            function updateCellHeadersRecursively(filters: (CompositeFilterDescriptor | FilterDescriptor)[]) {
                if (filters?.length > 0) {
                    filters.forEach(filter => {
                        if (isCompositeFilterDescriptor(filter)) {
                            // filter is CompositeFilterDescriptor
                            updateCellHeadersRecursively(filter.filters)
                        }
                        else {
                            // filter is FilterDescriptor
                            let column = props.columns.find(col => col.field === filter.field)
                            if (column != null) {
                                column.headerCell = "activeFilterTemplate"
                            }
                        }
                    })
                }
            }

            /********************************************************************************************************************
             WATCHERS
             ********************************************************************************************************************/
            watch(data, () => processData()) // data might not be available immediately on mount

            /********************************************************************************************************************
             LIFE-CYCLE HOOKS
             ********************************************************************************************************************/
            onMounted(() => initializeState())

            /********************************************************************************************************************
             COMPONENT INTERFACE
             ********************************************************************************************************************/
            return { ...publicData, ...computedData, ...publicMethods }
        }
    });
